import { useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from '@tanstack/react-router'
import { $api } from '../api/queryClient'
import type { TimelineEvent } from '../api/schema'
import { Badge } from './ui/badge'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip'
import { AppIcon } from './ui/icon'
import { mdiCalendarClock, mdiChevronLeft, mdiChevronRight, mdiHome, mdiLoading, mdiShieldCheck, mdiWrench } from '@mdi/js'
import { EmptyState } from './ui/empty-state'
import { mdiTimelineOutline } from '@mdi/js'
import { SECTION_ICONS } from '../lib/section-icons'

interface AssetMilestoneTimelineProps {
  org: string
  assetUid: string
}

const ICON_MAP: Record<string, string> = {
  mdiHome,
  mdiWrench,
  mdiShieldCheck,
  mdiFileDocument: SECTION_ICONS.documents,
  mdiFileSign: SECTION_ICONS.contracts,
  mdiCalendarClock,
}

const CATEGORY_DOT: Record<string, string> = {
  construction: 'bg-slate-500',
  property: 'bg-blue-500',
  compliance: 'bg-green-500',
  document: 'bg-amber-500',
  contract: 'bg-purple-500',
  capex: 'bg-emerald-500',
}

const STATUS_BADGE_CLASS: Record<string, string> = {
  compliant: 'bg-success/10 text-success border-success/25',
  non_compliant: 'bg-destructive/10 text-destructive border-destructive/25',
  planned: 'bg-info/10 text-info border-info/25',
  completed: 'bg-success/10 text-success border-success/25',
  expired: 'bg-destructive/10 text-destructive border-destructive/25',
}

const CATEGORIES = ['all', 'construction', 'property', 'compliance', 'document', 'contract', 'capex'] as const

function formatDateShort(dateStr: string, locale: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString(locale, { month: 'short', year: 'numeric' })
}

function formatDateFull(dateStr: string, locale: string): string {
  const d = new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' })
}

export function AssetMilestoneTimeline({ org, assetUid }: AssetMilestoneTimelineProps) {
  const { t, i18n } = useTranslation('assets')
  const navigate = useNavigate()
  const [activeCategory, setActiveCategory] = useState<string>('all')
  const scrollRef = useRef<HTMLDivElement>(null)
  const locale = i18n.language || 'en'

  const query: Record<string, any> = { include_future: true }
  if (activeCategory !== 'all') query.category = activeCategory

  const { data, isLoading } = $api.useQuery(
    'get',
    '/api/v1/organizations/{orgCode}/assets/{assetUid}/timeline' as any,
    {
      params: {
        path: { orgCode: org, assetUid },
        query,
      },
    },
  ) as { data: any; isLoading: boolean }

  const events: TimelineEvent[] = useMemo(
    () => (data?.data as TimelineEvent[]) || [],
    [data],
  )
  const todayStr = new Date().toISOString().slice(0, 10)

  const todayIndex = useMemo(() => {
    for (let i = 0; i < events.length; i++) {
      if (events[i].date >= todayStr) return i
    }
    return events.length
  }, [events, todayStr])

  const scroll = (dir: 'left' | 'right') => {
    scrollRef.current?.scrollBy({ left: dir === 'left' ? -300 : 300, behavior: 'smooth' })
  }

  function handleEventClick(event: TimelineEvent) {
    switch (event.source_type) {
      case 'document':
        navigate({ to: '/$org/documents/$docUid', params: { org, docUid: event.source_uid } })
        break
      case 'contract':
        navigate({ to: '/$org/contracts/$contractUid', params: { org, contractUid: event.source_uid } })
        break
      default:
        break
    }
  }

  if (isLoading) {
    return (
      <div className="bg-card rounded-lg border border-border p-6" data-testid="milestone-timeline">
        <div className="flex justify-center py-8">
          <AppIcon path={mdiLoading} size="lg" className="motion-safe:animate-spin" />
        </div>
      </div>
    )
  }

  if (events.length === 0 && activeCategory === 'all') {
    return (
      <div className="bg-card rounded-lg border border-border p-6" data-testid="milestone-timeline">
        <h3 className="text-lg font-semibold text-foreground mb-4">{t('timeline.title')}</h3>
        <EmptyState
          variant="inline"
          icon={mdiTimelineOutline}
          title={t('timeline.empty')}
          data-testid="milestone-timeline-empty"
        />
      </div>
    )
  }

  // Build items: events interleaved with a "today" marker
  type Item = { type: 'event'; event: TimelineEvent; idx: number } | { type: 'today' }
  const items: Item[] = []
  let todayInserted = false
  events.forEach((event, idx) => {
    if (!todayInserted && idx === todayIndex) {
      items.push({ type: 'today' })
      todayInserted = true
    }
    items.push({ type: 'event', event, idx })
  })
  if (!todayInserted) {
    items.push({ type: 'today' })
  }

  return (
    <div className="bg-card rounded-lg border border-border p-6" data-testid="milestone-timeline">
      {/* Header */}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-foreground">{t('timeline.title')}</h3>
        <div className="flex items-center gap-2">
          <div className="flex flex-wrap gap-1" data-testid="milestone-timeline-filters">
            {CATEGORIES.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                data-testid={`milestone-filter-${cat}`}
                className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                  activeCategory === cat
                    ? 'bg-primary text-primary-foreground'
                    : 'bg-muted text-muted-foreground hover:bg-muted/80'
                }`}
              >
                {t(`timeline.category_${cat}`)}
              </button>
            ))}
          </div>
          {/* Scroll arrows */}
          <div className="hidden sm:flex gap-1">
            <button onClick={() => scroll('left')} className="p-1 rounded hover:bg-muted" data-testid="milestone-scroll-left">
              <AppIcon path={mdiChevronLeft} size="sm" tone="muted" />
            </button>
            <button onClick={() => scroll('right')} className="p-1 rounded hover:bg-muted" data-testid="milestone-scroll-right">
              <AppIcon path={mdiChevronRight} size="sm" tone="muted" />
            </button>
          </div>
        </div>
      </div>

      {/* Horizontal timeline */}
      <TooltipProvider>
        <div
          ref={scrollRef}
          className="overflow-x-auto pb-2 scrollbar-thin"
          style={{ scrollbarWidth: 'thin' }}
        >
          <div className="relative flex items-start min-w-max pt-2">
            {/* Horizontal line */}
            <div className="absolute left-0 right-0 top-[26px] h-0.5 bg-border" />

            {items.map((item) => {
              if (item.type === 'today') {
                return (
                  <div key="today" className="relative flex flex-col items-center mx-2" style={{ minWidth: 60 }} data-testid="milestone-today-marker">
                    {/* Today dot */}
                    <div className="w-5 h-5 rounded-full bg-primary border-2 border-background z-10 flex items-center justify-center">
                      <div className="w-2 h-2 rounded-full bg-primary-foreground" />
                    </div>
                    <div className="mt-2 text-[10px] font-semibold text-primary uppercase tracking-wide whitespace-nowrap">
                      {t('timeline.today')}
                    </div>
                  </div>
                )
              }

              const { event, idx } = item
              const iconPath = ICON_MAP[event.icon] || mdiHome
              const dotColor = CATEGORY_DOT[event.category] || 'bg-slate-500'
              const isFuture = event.is_future

              return (
                <Tooltip key={`${event.source_uid}-${event.date}-${idx}`}>
                  <TooltipTrigger asChild>
                    <button
                      className={`relative flex flex-col items-center mx-1 group cursor-pointer ${isFuture ? 'opacity-50' : ''}`}
                      style={{ minWidth: 80, maxWidth: 110 }}
                      onClick={() => handleEventClick(event)}
                      data-testid="milestone-event"
                    >
                      {/* Dot */}
                      <div className={`w-3.5 h-3.5 rounded-full ${dotColor} border-2 border-background z-10 group-hover:scale-125 transition-transform`} />

                      {/* Icon + label below the line */}
                      <div className="mt-2 flex flex-col items-center gap-1">
                        <div className={`w-7 h-7 rounded-md flex items-center justify-center ${isFuture ? 'bg-muted/50' : 'bg-muted'}`}>
                          <AppIcon path={iconPath} size="xs" />
                        </div>
                        <span className="text-[10px] text-muted-foreground whitespace-nowrap">
                          {formatDateShort(event.date, locale)}
                        </span>
                        <span className={`text-[10px] font-medium text-center leading-tight line-clamp-2 ${isFuture ? 'text-muted-foreground' : 'text-foreground'}`} style={{ maxWidth: 100 }}>
                          {event.label}
                        </span>
                        {event.status && (
                          <Badge variant="outline" className={`text-[9px] px-1 py-0 ${STATUS_BADGE_CLASS[event.status] || ''}`}>
                            {t(`timeline.status_${event.status}`)}
                          </Badge>
                        )}
                      </div>
                    </button>
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-xs">
                    <div className="text-sm font-medium">{event.label}</div>
                    <div className="text-xs text-muted-foreground">
                      {formatDateFull(event.date, locale)}
                      {event.date_end && ` → ${formatDateFull(event.date_end, locale)}`}
                    </div>
                    {event.status && (
                      <div className="text-xs mt-0.5">{t(`timeline.status_${event.status}`)}</div>
                    )}
                  </TooltipContent>
                </Tooltip>
              )
            })}
          </div>
        </div>
      </TooltipProvider>
    </div>
  )
}
